/* eslint-disable @typescript-eslint/no-explicit-any */
import { Content } from '@tiptap/react';
import { StoryCardPostContentBlockFields } from '../../../services';
import {
  AudioBlockData,
  BlockTypes,
  CalloutBlockData,
  ChecklistBlockData,
  ChecklistBlockElement,
  CodeBlockData,
  DelimiterBlockData,
  EmbedLinkBlockData,
  FileBlockData,
  HeaderBlockData,
  ImageBlockData,
  ListBlockData,
  ListBlockElement,
  ListBlockStyle,
  ParagraphBlockData,
  PdfBlockData,
  QuoteBlockData,
  StoryBlockData,
  StoryCardBlockData,
  TableBlockData,
  UserBlockData,
  VideoBlockData,
} from '../../models';

interface TiptapMark {
  type: string;
  attrs?: Record<string, any>;
}

interface TiptapTextNode {
  type: 'text';
  text: string;
  marks?: TiptapMark[];
}

interface TiptapNode {
  type: string;
  attrs?: Record<string, any>;
  content?: (TiptapNode | TiptapTextNode)[];
}

interface TiptapDoc {
  type: 'doc';
  content: TiptapNode[];
}

interface BuildListArgs {
  type: string;
  items: ListBlockElement[];
}

interface ProcessNodeArgs {
  result: TiptapTextNode[];
  node: ChildNode;
  marks?: TiptapMark[];
}

const getMarkType = (tag: string) => {
  switch (tag) {
    case 'b':
    case 'strong':
      return 'bold';
    case 'i':
    case 'em':
      return 'italic';
    case 'u':
      return 'underline';
    case 'code':
      return 'code';
    case 'mark':
      return 'highlight';
    case 'sub':
      return 'subscript';
    case 'sup':
      return 'superscript';
    case 'a':
      return 'link';
  }
};

const processNode = ({ result, node, marks = [] }: ProcessNodeArgs) => {
  const { nodeType, textContent } = node;

  if (nodeType === Node.TEXT_NODE) {
    result.push({ type: 'text', text: textContent ?? '', marks });
  }

  if (nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement;

    const markType = getMarkType(element.tagName.toLowerCase());

    let marksCopy = [...marks];

    if (markType) {
      const attrs = markType === 'link' ? { attrs: { href: element.getAttribute('href') } } : {};

      marksCopy = [...marks, { type: markType, ...attrs }];
    }

    element.childNodes.forEach((node) => processNode({ result, node, marks: marksCopy }));
  }
};

function parseTextWithMarks(html: string): TiptapTextNode[] {
  const wrapper = document.createElement('div');

  wrapper.innerHTML = html;

  const result: TiptapTextNode[] = [];

  // function processNode(node: ChildNode, activeMarks: TiptapMark[] = []) {
  //   if (node.nodeType === Node.TEXT_NODE) {
  //     const text = node.textContent ?? '';
  //     if (text.trim()) {
  //       result.push({
  //         type: 'text',
  //         text,
  //         ...(activeMarks.length > 0 ? { marks: [...activeMarks] } : {}),
  //       });
  //     }
  //   }

  //   if (node.nodeType === Node.ELEMENT_NODE) {
  //     const element = node as HTMLElement;
  //     const tagName = element.tagName.toLowerCase();
  //     const markType = getMarkType(tagName);

  //     let newMarks = activeMarks;

  //     if (markType) {
  //       if (markType === 'link') {
  //         const href = element.getAttribute('href');
  //         if (href) {
  //           newMarks = [...activeMarks, { type: 'link', attrs: { href } }];
  //         }
  //       } else {
  //         newMarks = [...activeMarks, { type: markType }];
  //       }
  //     }

  //     element.childNodes.forEach((child) => processNode(child, newMarks));
  //   }
  // }

  wrapper.childNodes.forEach((node) => processNode({ result, node }));

  return result;
}

const paragraphToContent = ({ text }: ParagraphBlockData): TiptapNode => {
  return { type: 'paragraph', content: parseTextWithMarks(text) };
};

const headingToContent = ({ level, text }: HeaderBlockData): TiptapNode => {
  return { type: 'heading', attrs: { level }, content: parseTextWithMarks(text) };
};

const checkListToContent = ({ items }: ChecklistBlockData): TiptapNode => {
  return {
    type: 'taskList',
    content: items.map(({ checked, text }: ChecklistBlockElement) => ({
      type: 'taskItem',
      attrs: { checked },
      content: [{ type: 'paragraph', content: parseTextWithMarks(text) }],
    })),
  };
};

const buildList = ({ type, items }: BuildListArgs): TiptapNode => {
  return {
    type,
    content: items.map(({ content, items }) => {
      const node: TiptapNode = {
        type: 'listItem',
        content: [{ type: 'paragraph', content: parseTextWithMarks(content) }],
      };

      if (node.content && items && items.length > 0) {
        node.content.push(buildList({ type, items }));
      }

      return node;
    }),
  };
};

const listToContent = ({ style, items }: ListBlockData): TiptapNode =>
  buildList({
    type: style === ListBlockStyle.ordered ? 'orderedList' : 'bulletList',
    items,
  });

const calloutToContent = ({ icon, text }: CalloutBlockData): TiptapNode => {
  return { type: 'calloutNode', attrs: { icon }, content: parseTextWithMarks(text) };
};

const quoteToContent = (data: QuoteBlockData): TiptapNode => {
  return { type: 'quoteNode', attrs: data };
};

const codeToContent = (data: CodeBlockData): TiptapNode => {
  return { type: 'codeNode', attrs: data };
};

const delimiterToContent = (data: DelimiterBlockData): TiptapNode => {
  return { type: 'delimiterNode', attrs: data };
};

const embedLinkToContent = (data: EmbedLinkBlockData): TiptapNode => {
  return { type: 'embedLinkNode', attrs: data };
};

const userToContent = (data: UserBlockData): TiptapNode => {
  return { type: 'userNode', attrs: data };
};

const storyToContent = (data: StoryBlockData): TiptapNode => {
  return { type: 'storyNode', attrs: data };
};

const storyCardToContent = (data: StoryCardBlockData): TiptapNode => {
  return { type: 'storyCardNode', attrs: data };
};

const imageToContent = (data: ImageBlockData): TiptapNode => {
  return { type: 'imageNode', attrs: data };
};

const audioToContent = (data: AudioBlockData): TiptapNode => {
  return { type: 'audioNode', attrs: data };
};

const videoToContent = (data: VideoBlockData): TiptapNode => {
  return { type: 'videoNode', attrs: data };
};

const pdfToContent = (data: PdfBlockData): TiptapNode => {
  return { type: 'pdfNode', attrs: data };
};

const fileToContent = (data: FileBlockData): TiptapNode => {
  return { type: 'fileNode', attrs: data };
};

const tableToContent = (data: TableBlockData): TiptapNode => {
  const { withHeadings, content } = data;

  const rows: TiptapNode[] = content.map((row, rowIndex) => {
    const isHeader = withHeadings && rowIndex === 0;

    return {
      type: 'tableRow',
      content: row.map((cell) => ({
        type: isHeader ? 'tableHeader' : 'tableCell',
        content: [{ type: 'paragraph', content: parseTextWithMarks(cell) }],
      })),
    };
  });

  return { type: 'table', content: rows };
};

export const blocksToContentConverter = (blocks: StoryCardPostContentBlockFields[]): Content => {
  const tiptapContent: TiptapDoc = { type: 'doc', content: [] };

  for (const block of blocks) {
    const { type, data } = block;

    switch (type) {
      case BlockTypes.paragraph:
        tiptapContent.content.push(paragraphToContent(data as ParagraphBlockData));
        break;
      case BlockTypes.header:
        tiptapContent.content.push(headingToContent(data as HeaderBlockData));
        break;
      case BlockTypes.checklist:
        tiptapContent.content.push(checkListToContent(data as ChecklistBlockData));
        break;
      case BlockTypes.list:
        tiptapContent.content.push(listToContent(data as ListBlockData));
        break;
      case BlockTypes.callout:
        tiptapContent.content.push(calloutToContent(data as CalloutBlockData));
        break;
      case BlockTypes.quote:
        tiptapContent.content.push(quoteToContent(data as QuoteBlockData));
        break;
      case BlockTypes.code:
        tiptapContent.content.push(codeToContent(data as CodeBlockData));
        break;
      case BlockTypes.delimiter:
        tiptapContent.content.push(delimiterToContent(data as DelimiterBlockData));
        break;
      case BlockTypes.embedlink:
        tiptapContent.content.push(embedLinkToContent(data as EmbedLinkBlockData));
        break;
      case BlockTypes.user:
        tiptapContent.content.push(userToContent(data as UserBlockData));
        break;
      case BlockTypes.story:
        tiptapContent.content.push(storyToContent(data as StoryBlockData));
        break;
      case BlockTypes.card:
        tiptapContent.content.push(storyCardToContent(data as StoryCardBlockData));
        break;
      case BlockTypes.image:
        tiptapContent.content.push(imageToContent(data as ImageBlockData));
        break;
      case BlockTypes.audio:
        tiptapContent.content.push(audioToContent(data as AudioBlockData));
        break;
      case BlockTypes.video:
        tiptapContent.content.push(videoToContent(data as VideoBlockData));
        break;
      case BlockTypes.pdf:
        tiptapContent.content.push(pdfToContent(data as PdfBlockData));
        break;
      case BlockTypes.file:
        tiptapContent.content.push(fileToContent(data as FileBlockData));
        break;
      case BlockTypes.table:
        tiptapContent.content.push(tableToContent(data as TableBlockData));
        break;
    }
  }

  return tiptapContent;
};
